import { useInfiniteQuery, UseInfiniteQueryOptions, useQuery, UseQueryOptions } from '@tanstack/react-query';

import url from 'url';

import { get } from '@front/common/src/api/requester';
import {
  OrderReview,
  PageResult,
  PrestationReference,
  Pro,
  UniverseReference,
} from '@front/common/src/types/commons';
import { BasketState } from '@front/common/src/ducks/common/basket';
import { isWellnessReference } from '@front/common/src/utils/universes';
import { WELLNESS_UNIVERSES } from '@front/common/src/constants/universes';

import { OrderProAccount } from '../types/pro';

const KEY = 'pro_accounts';

type GetPreviousProsArgs = {
  universe: UniverseReference;
  zipCode?: string;
  referringPro?: string;
  prestations?: PrestationReference[];
  weeklyRecurrence?: BasketState['weekly_recurrence'];
  duration?: BasketState['duration'];
};

type GetProReviewsArgs = {
  universes: UniverseReference[];
  proPublicId: string;
  pageParam: string | undefined;
};

const api = {
  getPrevious: ({ universe, zipCode, referringPro, prestations, weeklyRecurrence, duration }: GetPreviousProsArgs) =>
    get('/customer/pro_accounts/previous', {
      query: {
        universe,
        zip_code: zipCode,
        referring_pro: referringPro,
        prestations,
        weekly_recurrence: weeklyRecurrence,
        duration,
      },
    }),
  getProReviews: ({ universes, proPublicId, pageParam }: GetProReviewsArgs) =>
    get(`/customer/pro_accounts/${proPublicId}/order_reviews`, {
      query: { universes, page: { size: 5 } },
      search: pageParam,
    }),
};

export const useGetPreviousPros = (
  args: GetPreviousProsArgs,
  options?: Omit<UseQueryOptions<Pro[], Error>, 'queryKey' | 'queryFn' | 'cacheTime' | 'staleTime'>,
) =>
  useQuery<Pro[], Error>({
    queryKey: [KEY, 'previous', args],
    queryFn: async () => {
      const response = await api.getPrevious(args);

      return response.data;
    },
    ...options,
  });

type GetProReviewsResponse = Pick<PageResult<OrderReview>, 'links'> & {
  data: {
    reviews: OrderReview[];
    average_rating: number;
    orders_delivered_count: number;
  };
  meta: { total: number };
};
type UseGetProReviewsInfiniteQueryParams = Pick<UseInfiniteQueryOptions<OrderReview>, 'enabled'> & {
  proAccount: OrderProAccount;
};

export const useGetProReviewsInfiniteQuery = ({ proAccount, enabled }: UseGetProReviewsInfiniteQueryParams) => {
  const { public_id: proPublicId, universe } = proAccount;
  const universes = isWellnessReference(universe) ? WELLNESS_UNIVERSES : [universe];

  const {
    data,
    fetchNextPage: fetchNextReviewsPage,
    error: fetchReviewsError,
    isFetchingNextPage: isFetchingNextReviewsPage,
    hasNextPage: hasMoreReviews,
    isInitialLoading: isInitialLoadingReviews,
  } = useInfiniteQuery<GetProReviewsResponse, Error>({
    queryKey: [KEY, proPublicId, 'order_reviews', 'infinite_reviews', { universes }],
    queryFn: ({ pageParam }) => api.getProReviews({ universes, proPublicId, pageParam }),
    getNextPageParam: (lastPage) => (lastPage.links.next ? url.parse(lastPage.links.next).search : undefined),
    enabled,
  });

  const reviews = data ? data.pages.flatMap((page) => page.data.reviews) : [];
  const totalReviews = data && data.pages.length > 0 ? data.pages[data.pages.length - 1].meta.total : 0;
  const averageRating = data && data.pages.length > 0 ? data.pages[data.pages.length - 1].data.average_rating : 0;
  const ordersCount =
    data && data.pages.length > 0 ? data.pages[data.pages.length - 1].data.orders_delivered_count : 0;

  return {
    reviews,
    totalReviews,
    fetchNextReviewsPage,
    fetchReviewsError,
    isFetchingNextReviewsPage,
    hasMoreReviews,
    isInitialLoadingReviews,
    averageRating,
    ordersCount,
  };
};
